import { Database } from './supabase';

export type Squad = 'U11' | 'U13' | 'U15' | 'U17';

export interface Coach {
  id: string;
  name: string;
  address: string;
  phone: string;
  profileImage?: string;
}

export interface Player {
  id: string;
  name: string;
  fatherName: string;
  motherName: string;
  fatherPhone: string;
  motherPhone: string;
  fatherEmail: string;
  motherEmail: string;
  dateOfBirth: string;
  address: string;
  subscriptionStatus: string;
  subscriptionValidTill: string;
  scholarshipPercentage: number;
  squad: string;
  aiffPlayerId?: string;
}

export interface Document {
  id: string;
  name: string;
  type: string;
  url: string;
  size: number;
  uploadedAt: string;
}

export type DocumentType = 'pdf' | 'image';

export interface DocumentAttachment {
  id: string;
  name: string;
  type: DocumentType;
  url: string;
  size: number;
  uploadedAt: string;
}

export interface Attendance {
  playerId: string;
  present: boolean;
  comments?: string;
}

export interface CoachAssignment {
  primary: string;
  secondary: string;
}

export interface TrialPlayer {
  name: string;
  comments: string;
}

export type AttendanceStatus = 'not-required' | 'pending' | 'marked';
export type SessionType = 'Training' | 'Tourney Match' | 'Practice Match';
export type GroundBookingType = 'Full Pitch' | 'Half Pitch';

export interface SessionDetails {
  id: string;
  date: string;
  time: string;
  duration: number;
  location: string;
  type: SessionType | string;
  groundBooking: GroundBookingType | string;
  squads: Squad[];
  attendance: Record<Squad, Attendance[]>;
  coaches: Record<Squad, CoachAssignment>;
  focus: Record<Squad, string>;
  documents: DocumentAttachment[];
  attendanceStatus: AttendanceStatus;
  locked?: boolean;
  trialPlayers?: TrialPlayer[];
}

// Report types
export type ReportType = 'planning' | 'attendance';
export type AttendanceReportSubject = 'coach' | 'player';

// Session summary stats (returned by reportService.getSessionSummaryStats)
export type SquadStat = {
  squad: string;
  displayName: string;
  attendance: {
    present: number;
    absent: number;
    total: number;
    percentage: number;
  };
  coaches: {
    primary: string;
    secondary: string;
  };
  players: {
    id: string;
    name: string;
    present: boolean;
  }[];
};

export type SessionSummaryStats = {
  id: string;
  date: string;
  time: string;
  duration: number;
  location: string;
  type: string;
  groundBooking: string;
  squadStats: SquadStat[];
  sessionPlans?: Record<string, string>;
  trialPlayers?: { name: string; comments: string }[];
  documents?: { id: string; name: string; type: string; url: string; size: number; uploadedAt: string }[];
};

// Factory helpers — always return fresh objects to avoid shared-reference bugs
export const emptySquadAttendance = (): Record<Squad, Attendance[]> => ({
  U11: [], U13: [], U15: [], U17: []
});

export const emptySquadCoaches = (): Record<Squad, CoachAssignment> => ({
  U11: { primary: '', secondary: '' },
  U13: { primary: '', secondary: '' },
  U15: { primary: '', secondary: '' },
  U17: { primary: '', secondary: '' },
});

export const emptySquadFocus = (): Record<Squad, string> => ({
  U11: '', U13: '', U15: '', U17: ''
});

// Re-export Database type for convenience
export type { Database };
